<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';
import {
	HEADER_OFFSET_CONFIG,
	TRANSITION_CONFIG
} from '~/composables/useDragToBottom/constants';
import { screenSizesInPixels } from '~~/utils';
import { PROPERTIES_LISTING_METHODS } from '~~/utils/symbols';

const route = useRoute();
const isMapView = computed<boolean>(() => route.path.includes('/imoveis'));

// Access shared header drag offset from page
const headerDragOffsetY = useState<number>(
	'header-drag-offset-y',
	() => HEADER_OFFSET_CONFIG.MIN_OFFSET
);

// Client-only flag to prevent hydration mismatch
const isClientMounted = ref(false);

// Setup mobile detection using VueUse breakpoints and shared sizes
const breakpoints = useBreakpoints(screenSizesInPixels);
const isMobileScreen = breakpoints.smaller('lg');

// Compute dynamic header container height - only for mobile and after client mount
const headerContainerHeight = computed(() => {
	if (isClientMounted.value && isMobileScreen.value) {
		// Calculate the visible height of the header based on the offset
		const offset = Math.max(
			HEADER_OFFSET_CONFIG.MIN_OFFSET,
			headerDragOffsetY.value || HEADER_OFFSET_CONFIG.MIN_OFFSET
		);
		// Only apply offset if it's meaningful (greater than threshold) to prevent micro-adjustments
		const effectiveOffset =
			offset > HEADER_OFFSET_CONFIG.HIDE_THRESHOLD
				? offset
				: HEADER_OFFSET_CONFIG.MIN_OFFSET;
		const mobileHeight = Math.max(
			HEADER_OFFSET_CONFIG.MIN_OFFSET,
			HEADER_OFFSET_CONFIG.MOBILE_HEIGHT - effectiveOffset
		);

		return { mobile: mobileHeight };
	}

	// Always return null during SSR and initial hydration
	return { mobile: null };
});

// Initialize flags after mount and keep header offset in sync with breakpoint
onMounted(() => {
	isClientMounted.value = true;
	headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
});

watch(isMobileScreen, isNowMobile => {
	if (!isNowMobile) {
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
	}
});

const { variant, transform, forceFinalVariant } = useHeaderAnimation({
	headerHeightInPx: HEADER_OFFSET_CONFIG.DESKTOP_HEIGHT,
	variants: ['primary', 'primary']
});

const headerContainerRef = useTemplateRef<HTMLElement>('headerContainerRef');

watch(
	() => headerContainerHeight.value.mobile,
	mobileHeight => {
		if (import.meta.server) {
			return;
		}
		const headerContainerEl = headerContainerRef.value;
		if (!headerContainerEl) {
			return;
		}
		if (mobileHeight !== null) {
			headerContainerEl.style.height = `${mobileHeight}px`;
		} else {
			headerContainerEl.style.removeProperty('height');
		}
		headerContainerEl.style.transition = `height ${TRANSITION_CONFIG.DURATION} ${TRANSITION_CONFIG.TIMING_FUNCTION}`;
		headerContainerEl.style.setProperty(
			'--mobile-header-height',
			`${HEADER_OFFSET_CONFIG.MOBILE_HEIGHT}px`
		);
		headerContainerEl.style.setProperty(
			'--desktop-header-height',
			`${HEADER_OFFSET_CONFIG.DESKTOP_HEIGHT}px`
		);
	},
	{ immediate: true }
);

useState<PropertiesListingMapState>('properties-listing-map', () => ({
	fetchedData: null,
	filters: {
		transactionType: 'sell',
		countPluscodes: [],
		propertyTypes: [],
		locationSlug: 'sao-paulo-sp-brasil'
	},
	pluscodes: {},
	mapConfig: {
		zoom: 14,
		maxZoom: 17,
		minZoom: 8,
		bounds: [],
		center: {
			lat: -23.585,
			lng: -46.685
		}
	}
}));

// Provide empty methods to avoid injection errors
// The actual implementation will be in the page component
provide(PROPERTIES_LISTING_METHODS, {
	updateMapFilters: () => {}
});
</script>

<template>
	<div class="flex h-dvh flex-col overflow-hidden">
		<div
			ref="headerContainerRef"
			:class="[
				'lg:overflow-visible! lg:h-(--desktop-header-height)',
				{ 'overflow-hidden': headerContainerHeight.mobile !== null }
			]"
		>
			<PortalHeader
				:variant="variant"
				:transform="transform"
				:is-visible="isMapView"
				:fixed="isMapView"
				:class="[
					'h-(--mobile-header-height) lg:h-(--desktop-header-height)',
					{
						'sticky flex': isMapView,
						fixed: !isMapView
					}
				]"
				@nav-option-being-shown="forceFinalVariant = true"
				@nav-option-closed="forceFinalVariant = false"
			/>
		</div>
		<div id="filters-bar" :class="{ 'mt-20': !isMapView }" />

		<div id="filters-sidebar" />

		<main
			class="flex grow flex-col"
			:class="{
				'relative h-[calc(100dvh-5rem-5.1875rem)] md:py-0': isMapView
			}"
		>
			<slot />
		</main>
	</div>
</template>
